import React, { ReactElement, useState, useRef, useLayoutEffect, useMemo } from 'react';
import { getLabelFromCode } from 'utils/ClassUtils';
import { Box, ButtonBase, Menu, MenuItem, useTheme, SxProps, Theme } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

/**
 * OverflowFilterBar renders a horizontal list of filter buttons with an overflow menu.
 * Filters that do not fit in the container are shown in the More menu.
 */
/**
 * OverflowFilterBar renders a horizontal list of filter buttons with an overflow menu.
 * Filters that do not fit in the container are shown in the More menu.
 * containerSx, itemSx, moreSx allow custom styling of the strip, items, and overflow button.
 */
export interface OverflowFilterBarProps {
  /** Array of filter options with title and identifiers */
  filters: Array<{ titleClass: string; titleLingo: string; }>;
  /** Currently selected filter (by titleClass and titleLingo) or null */
  selectedFilter: { titleClass: string; titleLingo: string } | null;
  /** Callback when a filter is selected or cleared (null) */
  onFilterSelect: (filter: { titleClass: string; titleLingo: string } | null) => void;
  /** Optional sx for the visible strip container */
  containerSx?: SxProps<Theme>;
  /** Optional sx override for each item (overrides defaultItem) */
  itemSx?: SxProps<Theme>;
  /** Optional sx override for the overflow More button */
  moreSx?: SxProps<Theme>;
  /** Optional icon to render inline after each item label */
  itemIcon?: React.ReactNode;
}

// Forward ref to container div for parent measurements
const OverflowFilterBar = React.forwardRef<HTMLDivElement, OverflowFilterBarProps>(
  (
    { filters, selectedFilter, onFilterSelect, containerSx, itemSx, moreSx, itemIcon },
    ref
  ): ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null);
  // expose containerRef to parent via forwarded ref
  React.useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);
  const theme = useTheme();
  // Refs for measuring all filters and the more button
  const measureRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const measureMoreRef = useRef<HTMLButtonElement>(null);
  const [visibleCount, setVisibleCount] = useState<number>(filters.length);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  // Derive the full ordered list, putting selectedFilter first if present
  const orderedFilters = useMemo(() => {
    if (selectedFilter) {
      const match = filters.find(
        f => f.titleClass === selectedFilter.titleClass && f.titleLingo === selectedFilter.titleLingo
      );
      if (match) {
        return [match, ...filters.filter(
          f => !(f.titleClass === selectedFilter.titleClass && f.titleLingo === selectedFilter.titleLingo)
        )];
      }
    }
    return filters;
  }, [filters, selectedFilter]);

  // Measure available space and button widths to determine how many fit
  useLayoutEffect(() => {
    const update = () => {
      const containerEl = containerRef.current;
      if (!containerEl) return;
      const style = window.getComputedStyle(containerEl);
      const padLeft = parseFloat(style.paddingLeft) || 0;
      const padRight = parseFloat(style.paddingRight) || 0;
      // available inner width for items
      const availableWidth = containerEl.clientWidth - padLeft - padRight;
      // Gap between items may be set via container gap; fall back to default spacing(1)
      const gapPx = parseFloat(style.gap) || parseFloat(theme.spacing(1));
      // widths of each filter, adding gap before items after the first
      const widthsRaw = measureRefs.current.map(ref => ref?.offsetWidth || 0);
      const widthsGap = widthsRaw.map((w, i) => (i === 0 ? w : w + gapPx));
      // overflow button width plus preceding gap
      const moreRaw = measureMoreRef.current?.offsetWidth || 0;
      const moreItemWidth = moreRaw + gapPx;
      // if all items fit
      const totalWidthAll = widthsGap.reduce((sum, w) => sum + w, 0);
      if (totalWidthAll <= availableWidth) {
        setVisibleCount(orderedFilters.length);
        return;
      }
      // otherwise find max items that fit with overflow button
      let used = 0;
      let count = 0;
      for (const w of widthsGap) {
        if (used + w + moreItemWidth <= availableWidth) {
          used += w;
          count++;
        } else {
          break;
        }
      }
      setVisibleCount(count);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [orderedFilters, theme]);

  const visible = orderedFilters.slice(0, visibleCount);
  const overflow = orderedFilters.slice(visibleCount);
  // Sort overflow menu items alphabetically by translated label
  const overflowMenuItems = useMemo(
    () => [...overflow].sort((a, b) =>
      getLabelFromCode(a.titleClass).localeCompare(
        getLabelFromCode(b.titleClass)
      )
    ),
    [overflow]
  );

  const handleFilter = (f: { titleClass: string; titleLingo: string }) => {
    if (selectedFilter && f.titleClass === selectedFilter.titleClass && f.titleLingo === selectedFilter.titleLingo) {
      onFilterSelect(null);
    } else {
      onFilterSelect({ titleClass: f.titleClass, titleLingo: f.titleLingo });
    }
    setMenuAnchor(null);
  };

  return (
    <>
      {/* Hidden measurement container */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          visibility: 'hidden',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          zIndex: -1,
        }}
      >
        {orderedFilters.map((f, i) => (
          <ButtonBase
            key={`measure-${f.titleClass}-${f.titleLingo}-${i}`}
            ref={el => { measureRefs.current[i] = el; }}
            sx={({ spacing }) => ({
              flex: '0 0 auto',
              px: spacing(2),
              py: spacing(1),
              borderRadius: 1,
              textTransform: 'none',
            })}
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
              {getLabelFromCode(f.titleClass)}
              {itemIcon && (
                <Box component="span" sx={{ ml: 0.5 }}>
                  {itemIcon}
                </Box>
              )}
            </Box>
          </ButtonBase>
        ))}
        <ButtonBase
          ref={measureMoreRef}
          sx={({ spacing }) => ({
            flex: '0 0 auto',
            px: spacing(2),
            py: spacing(1),
            borderRadius: 1,
            textTransform: 'none',
          })}
        >
          <MoreHorizIcon />
        </ButtonBase>
      </Box>
      {/* Visible strip */}
      <Box
        ref={containerRef}
        sx={theme => {
          const base = {
            px: { xs: 1, sm: 2 },
            mb: 1,
            display: 'flex',
            overflowX: 'hidden',
            gap: theme.spacing(1),
            bgcolor: 'transparent',
            py: theme.spacing(0.5),
            // hide any scrollbars entirely
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none', /* IE and Edge */
            scrollbarWidth: 'none', /* Firefox */
          } as any;
          const override = typeof containerSx === 'function'
            ? containerSx(theme as Theme)
            : containerSx || {};
          return { ...base, ...override };
        }}
      >
        {visible.map((f, i) => {
          const active =
            selectedFilter?.titleClass === f.titleClass &&
            selectedFilter?.titleLingo === f.titleLingo;
          const defaultItem = (theme: Theme) => ({
            flex: '0 0 auto',
            height: theme.spacing(4),  // enforce consistent height (padding-box)
            px: theme.spacing(2),
            py: theme.spacing(1),
            borderRadius: 1,
            bgcolor: active ? theme.palette.primary.main : theme.palette.grey[100],
            color: active ? theme.palette.primary.contrastText : theme.palette.text.primary,
            textTransform: 'none',
            alignItems: 'center',
            '&:hover': {
              bgcolor: active ? theme.palette.primary.dark : theme.palette.grey[200],
            },
          });
          const overrideItem = itemSx;
          return (
            <ButtonBase
              key={`filter-${f.titleClass}-${f.titleLingo}-${i}`}
              onClick={() => handleFilter(f)}
              sx={
                ((theme: Theme) => ({ ...defaultItem(theme), ...(overrideItem || {}) })) as SxProps<Theme>
              }
            >
              <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                {getLabelFromCode(f.titleClass)}
                {itemIcon && <Box component="span" sx={{ ml: 0.5 }}>{itemIcon}</Box>}
              </Box>
            </ButtonBase>
          );
        })}
        {overflow.length > 0 && (
          <ButtonBase
            onClick={e => setMenuAnchor(e.currentTarget)}
            sx={
              ((theme: Theme) => {
                const base = {
                  flex: '0 0 auto',
                  height: theme.spacing(4),  // match item height
                  px: theme.spacing(2),
                  py: theme.spacing(1),
                  borderRadius: 1,
                  bgcolor: theme.palette.grey[100],
                  color: theme.palette.text.primary,
                  textTransform: 'none',
                  alignItems: 'center',
                  '&:hover': { bgcolor: theme.palette.grey[200] },
                } as any;
                const override = moreSx || {};
                return { ...base, ...override };
              }) as SxProps<Theme>
            }
          >
            <MoreHorizIcon />
          </ButtonBase>
        )}
      </Box>
      {/* Overflow menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
      {overflowMenuItems.map((f, i) => {
          const active = selectedFilter?.titleClass === f.titleClass && selectedFilter?.titleLingo === f.titleLingo;
          return (
            <MenuItem
              key={`menu-${f.titleClass}-${f.titleLingo}-${i}`}
              selected={active}
              onClick={() => handleFilter(f)}
            >
              {getLabelFromCode(f.titleClass)}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
});

export default OverflowFilterBar;